import React, { useEffect, useState, useCallback } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import {
  Box,
  Flex,
  VStack,
  Button,
  Text,
  Image,
  useDisclosure,
  Drawer,
  DrawerOverlay,
  DrawerContent,
  IconButton,
  useColorMode,
} from '@chakra-ui/react';
import {
  FiCalendar,
  FiHelpCircle,
  FiMenu,
  FiChevronLeft,
  FiChevronRight,
  FiLogOut,
  FiSettings,
  FiCpu,
  FiGlobe,
  FiMessageSquare,
  FiMessageCircle,
  FiUsers,
} from 'react-icons/fi';
import {
  MdOutlineAssignment,
  MdOutlineContacts,
  MdOutlinePhoneCallback,
  MdOutlineSupportAgent,
} from 'react-icons/md';
import { useSidebar } from '../hooks/useSidebar';
import PageTransition from '../components/ui/PageTransition';
import AppVersion from '../components/AppVersion';
import AssistantDrawer from '../components/workspace/AssistantDrawer';
import NotificationBell from '../components/workspace/NotificationBell';
import { WorkspaceAssistantProvider } from '../components/workspace/WorkspaceAssistantContext';
import { useTenant } from '../contexts/TenantContext';
import { useLanguage, switchLang } from '../i18n';
import { getSidebarWidth, setSidebarWidth as persistSidebarWidth } from '../utils/storage';
import { ChatWidgetAPI } from '../services/api';
import { followupCopy } from '../pages/User/callbackExperience';
import { usePendingCallbackCount } from '../hooks/usePendingCallbackCount';
import { useAwaitingChatCount } from '../hooks/useAwaitingChatCount';
import { filterPortalMenuItems, groupPortalMenuItems, isInternalPortalToolsEnabled } from '../utils/portalAccess';
import { useEmbeddedWorkspaceHeight } from '../utils/embeddedWorkspace';

const BRAND_LOGO_SRC = `${process.env.PUBLIC_URL || ''}/bizsprite-logo.png`;

const menuItems = [
  { key: 'settings',          icon: FiSettings,  labelKey: 'nav_settings',          path: '/app/settings',        group: 'aiKnowledge' },
  { key: 'extensions',        icon: MdOutlineSupportAgent, labelKey: 'nav_extensions',        path: '/app/extensions', group: 'userManagement' },
  { key: 'contacts',          icon: MdOutlineContacts, labelKey: 'nav_contacts',          path: '/app/contacts',     group: 'aiKnowledge' },
  { key: 'call-history',      icon: MdOutlinePhoneCallback, labelKey: 'nav_call_history',      path: '/app/call-history', group: 'userManagement' },
  // Website Chatbox conversations; shown only when this account has turned the chat on.
  { key: 'chat-records',      icon: FiMessageCircle, labelKey: 'nav_chat_records',      path: '/app/chat-records', requiresChatWidget: true, group: 'userManagement' },
  { key: 'customers',         icon: FiUsers,     labelKey: 'nav_customers',         path: '/app/customers',    group: 'userManagement' },
  { key: 'callback-requests', icon: MdOutlineAssignment, labelKey: 'nav_callback_requests', path: '/app/callback-requests', group: 'userManagement' },
  { key: 'calendar', icon: FiCalendar, labelKey: 'nav_calendar', path: '/app/calendar', requiresFeature: 'booking', group: 'userManagement' },
  { key: 'sms',               icon: FiMessageSquare, labelKey: 'nav_sms_logs',       path: '/app/sms',             requiresFeature: 'sms', group: 'userManagement' },
  { key: 'qa',                icon: FiHelpCircle, labelKey: 'nav_qa',                path: '/app/qa',              requiresFeature: 'qa', group: 'aiKnowledge' },
  { key: 'ai-test',           icon: FiCpu,        labelKey: 'nav_ai_test',           path: '/app/ai-test', internalOnly: true, group: 'userManagement' },
];

const UserLayoutShell = () => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const location = useLocation();
  const { setColorMode } = useColorMode();
  const { isCollapsed, toggle } = useSidebar();
  const { getCurrentTenantInfo, logout, entitlements, userRole } = useTenant();
  const { t, lang } = useLanguage();
  const isEmbedded = window.parent !== window;
  const embeddedHeight = useEmbeddedWorkspaceHeight(isEmbedded);
  // Keep only the current in-app path; SSO credentials never leave in a new-window URL.
  const standalonePath = location.pathname;
  const tenantInfo = getCurrentTenantInfo();
  const [hasChatWidget, setHasChatWidget] = useState(false);
  useEffect(() => {
    let live = true;
    ChatWidgetAPI.get().then(({ widget }) => live && setHasChatWidget(Boolean(widget?.enabled))).catch(() => {});
    return () => { live = false; };
  }, []);
  const visibleMenuItems = filterPortalMenuItems(
    menuItems.filter((item) => !item.requiresChatWidget || hasChatWidget),
    entitlements,
    isInternalPortalToolsEnabled({ role: userRole }),
  );
  const groupedMenuItems = groupPortalMenuItems(visibleMenuItems);
  const pendingCallbacks = usePendingCallbackCount();
  // Website chats waiting for a person (W-H); only polled when the chat is turned on.
  const awaitingChats = useAwaitingChatCount(hasChatWidget);
  const menuBadges = { 'callback-requests': pendingCallbacks, 'chat-records': awaitingChats };
  const activeMenuItem = visibleMenuItems.find((item) => location.pathname.startsWith(item.path));
  const pageTitle = activeMenuItem ? t(activeMenuItem.labelKey) : '';

  // Resizable sidebar state (versioned localStorage)
  const [sidebarWidth, setSidebarWidth] = useState(() => getSidebarWidth(280));
  const [isResizing, setIsResizing] = useState(false);

  // Set the color mode to light once per app load (avoid re-running on every mount)
  useEffect(() => {
    if (window.__aikerColorModeSet) return;
    window.__aikerColorModeSet = true;
    setColorMode('light');
  }, [setColorMode]);

  // Save sidebar width to localStorage
  useEffect(() => {
    persistSidebarWidth(sidebarWidth);
  }, [sidebarWidth]);

  // Handle sidebar resize
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setIsResizing(true);
  }, []);

  const resizeRef = React.useRef(null);
  const handleMouseMove = useCallback((e) => {
    if (!isResizing) return;
    const newWidth = e.clientX;
    if (newWidth < 200 || newWidth > 500) return;
    if (resizeRef.current) cancelAnimationFrame(resizeRef.current);
    resizeRef.current = requestAnimationFrame(() => {
      setSidebarWidth(newWidth);
      resizeRef.current = null;
    });
  }, [isResizing]);

  const handleMouseUp = useCallback(() => {
    setIsResizing(false);
  }, []);

  useEffect(() => {
    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing, handleMouseMove, handleMouseUp]);

  const handleLogout = () => {
    logout(); // The session owner navigates only after server revocation succeeds.
  };

  const SidebarContent = () => (
    <Box h="100%" display="flex" flexDirection="column" bg="white" borderRight="1px" borderColor="gray.100">
      {/* Logo Area */}
      <Flex h="20" alignItems="center" px={isCollapsed ? 4 : 8} borderBottom="1px" borderColor="gray.100" justifyContent={isCollapsed ? "center" : "flex-start"}>
        <Image
          src={BRAND_LOGO_SRC}
          alt="Bizsprite"
          h={isCollapsed ? '24px' : '38px'}
          w={isCollapsed ? '56px' : '154px'}
          objectFit="contain"
          bg="white"
        />
      </Flex>

      {/* Menu Items - scroll inside sidebar when many items or short viewport */}
      <VStack spacing={1} align="stretch" px={4} py={6} flex="1" minH={0} overflowY="auto">
        {groupedMenuItems.map((group) => (
          <Box key={group.key} w="full">
            {!isCollapsed && (
              <Text px={2} pt={3} pb={1} fontSize="xs" fontWeight="semibold" color="gray.600" textTransform="uppercase" letterSpacing="wider" noOfLines={1}>
                {t(group.labelKey)}
              </Text>
            )}
            <VStack spacing={1} align="stretch">
              {group.items.map((item) => {
                const isActive = location.pathname === item.path;
                const Icon = item.icon;
                const label = t(item.labelKey);
                const badgeCount = menuBadges[item.key] || 0;
                const badgeLabel = badgeCount > 0 ? followupCopy(lang).pendingBadge(badgeCount) : '';
                const badgeText = badgeCount > 99 ? '99+' : String(badgeCount);
                return (
                  <Button
                    key={item.key}
                    as={Link}
                    to={item.path}
                    onClick={onClose}
                    variant="ghost"
                    justifyContent={isCollapsed ? "center" : "flex-start"}
                    alignItems="center"
                    color={isActive ? "blue.600" : "gray.600"}
                    bg={isActive ? "blue.50" : "transparent"}
                    _hover={{ bg: isActive ? "blue.50" : "gray.50", color: isActive ? "blue.600" : "gray.900" }}
                    leftIcon={
                      <Box as="span" display="inline-flex" alignItems="center" justifyContent="center" boxSize="22px" flexShrink={0}>
                        <Icon size={20} />
                      </Box>
                    }
                    iconSpacing={isCollapsed ? 0 : 3}
                    size="lg"
                    w="full"
                    minW={0}
                    px={isCollapsed ? 0 : 4}
                    position="relative"
                    overflow="hidden"
                    title={badgeLabel ? `${label} · ${badgeLabel}` : label}
                    aria-label={badgeLabel ? `${label}, ${badgeLabel}` : undefined}
                  >
                    {!isCollapsed && (
                      <Text
                        as="span"
                        fontSize="md"
                        fontWeight="medium"
                        lineHeight="1.2"
                        minW={0}
                        flex="1"
                        noOfLines={1}
                        textAlign="left"
                      >
                        {label}
                      </Text>
                    )}
                    {badgeCount > 0 && (
                      <Box
                        as="span"
                        data-testid={`nav-badge-${item.key}`}
                        aria-hidden="true"
                        bg="red.500"
                        color="white"
                        fontSize="xs"
                        fontWeight="bold"
                        lineHeight="18px"
                        minW="18px"
                        h="18px"
                        px={1}
                        borderRadius="full"
                        textAlign="center"
                        flexShrink={0}
                        ml={isCollapsed ? 0 : 2}
                        mr={isCollapsed ? 0 : 1}
                        position={isCollapsed ? 'absolute' : 'static'}
                        top={isCollapsed ? '4px' : undefined}
                        right={isCollapsed ? '8px' : undefined}
                      >
                        {badgeText}
                      </Box>
                    )}
                    {isActive && (
                      <Box
                        position="absolute"
                        right="0"
                        top="50%"
                        transform="translateY(-50%)"
                        w="4px"
                        h="24px"
                        bg="blue.600"
                        borderLeftRadius="md"
                      />
                    )}
                  </Button>
                );
              })}
            </VStack>
          </Box>
        ))}
      </VStack>

      {/* User & Footer */}
      <Box p={4} borderTop="1px" borderColor="gray.100">
        <VStack spacing={4} align="stretch">
          {!isCollapsed && (
            <Box px={2}>
              <Text fontSize="sm" fontWeight="medium" color="gray.900" noOfLines={1}>
                {tenantInfo?.name || t('tenant_user')}
              </Text>
            </Box>
          )}
          <NotificationBell zh={lang === 'zh'} showLabel={!isCollapsed} />
          <Button
            variant="ghost"
            color="gray.500"
            _hover={{ bg: 'gray.50', color: 'gray.700' }}
            leftIcon={<FiGlobe size={16} />}
            iconSpacing={isCollapsed ? 0 : 3}
            justifyContent={isCollapsed ? "center" : "flex-start"}
            size="sm"
            w="full"
            onClick={switchLang}
          >
            {!isCollapsed && t('lang_label')}
          </Button>
          <Button
            variant="ghost"
            color="red.500"
            _hover={{ bg: 'red.50' }}
            leftIcon={<FiLogOut size={20} />}
            iconSpacing={isCollapsed ? 0 : 3}
            justifyContent={isCollapsed ? "center" : "flex-start"}
            onClick={handleLogout}
          >
            {!isCollapsed && t('sign_out')}
          </Button>
        </VStack>
      </Box>
    </Box>
  );

  return (
    <Flex className="aiker-frontdesk" data-testid="frontdesk-workspace" h={embeddedHeight ? `${embeddedHeight}px` : '100dvh'} minH={0} bg="gray.50" overflow="hidden">
      {/* Desktop Sidebar */}
      <Box
        display={{ base: 'none', md: 'block' }}
        w={isCollapsed ? "80px" : `${sidebarWidth}px`}
        transition={isResizing ? "none" : "width 0.2s"}
        position="relative"
        flexShrink={0}
      >
        <SidebarContent />
        {/* Collapse Button */}
        <IconButton
          aria-label={isCollapsed ? t('expand_sidebar') : t('collapse_sidebar')}
          icon={isCollapsed ? <FiChevronRight /> : <FiChevronLeft />}
          onClick={toggle}
          position="absolute"
          right="-12px"
          top="28px"
          size="xs"
          colorScheme="blue"
          isRound
          zIndex="20"
          boxShadow="md"
        />
        {/* Resize Handle */}
        {!isCollapsed && (
          <Box
            position="absolute"
            right="0"
            top="0"
            bottom="0"
            w="4px"
            cursor="col-resize"
            _hover={{ bg: "blue.200" }}
            bg={isResizing ? "blue.400" : "transparent"}
            onMouseDown={handleMouseDown}
            zIndex="10"
          />
        )}
      </Box>

      {/* Mobile Drawer */}
      <Drawer isOpen={isOpen} placement="left" onClose={onClose} size="xs">
        <DrawerOverlay />
        <DrawerContent>
          <SidebarContent />
        </DrawerContent>
      </Drawer>

      {/* Main Content */}
      <Flex flex="1" flexDirection="column" overflow="hidden" w="0" minH={0}>
        {isEmbedded && (
          <Box flexShrink={0} px={4} py={2} bg="white" borderBottom="1px" borderColor="gray.100">
            <Button as="a" href={standalonePath} target="_blank" rel="noopener noreferrer" size="sm" variant="link" colorScheme="blue">
              {lang === 'zh' ? '在新窗口打开完整后台' : 'Open full workspace in a new window'}
            </Button>
          </Box>
        )}
        {/* Mobile Header */}
        <Flex
          display={{ base: 'flex', md: 'none' }}
          h="16"
          alignItems="center"
          bg="white"
          px="4"
          borderBottom="1px"
          borderColor="gray.200"
          justifyContent="space-between"
        >
          <IconButton
            variant="ghost"
            onClick={onOpen}
            icon={<FiMenu size={24} />}
            aria-label={t('open_menu')}
          />
          <Image src={BRAND_LOGO_SRC} alt="Bizsprite" h="34px" w="138px" objectFit="contain" bg="white" />
          <Box w={8}><NotificationBell zh={lang === 'zh'} placement="bottom-end" /></Box>
        </Flex>

        {/* The task region scrolls natural-flow pages; bounded lists may own their inner scroll. */}
        <Box flex="1" minH={0} overflow="hidden" p={{ base: 4, md: 6 }} position="relative" display="flex" flexDirection="column">
          <Box maxW="1280px" mx="auto" flex="1" minH={0} display="flex" flexDirection="column" w="100%">
            <PageTransition>
              {location.state?.workspaceNotice && <Text role="status" mb={4}>{location.state.workspaceNotice}</Text>}
              <Outlet />
            </PageTransition>
          </Box>
          {/* Version: bottom-right, subtle */}
          <Flex position="absolute" bottom={2} right={4} justify="flex-end">
            <AppVersion />
          </Flex>
          <AssistantDrawer zh={lang === 'zh'} pageTitle={pageTitle} />
        </Box>
      </Flex>
    </Flex>
  );
};

// The provider sits above the shell so the sidebar bell, the assistant and the
// pages' PageHelp blocks share one notification feed and page context.
const UserLayout = () => (
  <WorkspaceAssistantProvider>
    <UserLayoutShell />
  </WorkspaceAssistantProvider>
);

export default UserLayout;
